/**
 * Host Types for Electron API
 * Tipos para interacción con el sistema operativo host
 */

// ========== Power Management Types ==========

/**
 * Acciones de power management disponibles
 */
export enum PowerAction {
  SHUTDOWN = 'shutdown',
  RESTART = 'restart',
  LOGOUT = 'logout',
  SLEEP = 'sleep',
  HIBERNATE = 'hibernate',
}

/**
 * Opciones para acciones de power management
 */
export interface PowerOptions {
  /** Forzar la acción (cierra aplicaciones sin guardar) */
  force?: boolean;
  /** Delay en segundos antes de ejecutar (solo Windows shutdown/restart) */
  delay?: number;
  /** Mensaje a mostrar (solo Windows shutdown/restart) */
  message?: string;
}

/**
 * Resultado de una acción de power management
 */
export interface PowerResult {
  success: boolean;
  action: PowerAction;
  error?: string;
  /** Indica si el sistema soporta esta acción */
  supported?: boolean;
}

// ========== Process Management Types ==========

/**
 * Resultado de matar un proceso
 */
export interface KillResult {
  success: boolean;
  processName: string;
  error?: string;
  /** Número de procesos terminados (si aplica) */
  killed?: number;
}

/**
 * Resultado de abrir un programa
 */
export interface OpenProgramResult {
  success: boolean;
  error?: string;
}

// ========== Host API Interface ==========

/**
 * Host API interface exposed to renderer
 * API para interactuar con el host/sistema operativo
 */
export interface HostAPI {
  // ========== Program Execution ==========

  /**
   * Abre un programa/ejecutable en la ruta especificada
   * @param path - Ruta al programa
   */
  openProgram: (path?: string) => Promise<OpenProgramResult>;

  /**
   * Mata un proceso por nombre
   * @param processName - Nombre del proceso (e.g., "notepad.exe", "firefox")
   */
  killProcess: (processName: string) => Promise<KillResult>;

  /**
   * Cierra un programa por su ruta ejecutable
   * Soporta variables de entorno, shortcuts .lnk (Windows), y rutas con espacios
   * @param programPath - Ruta al programa
   */
  closeProgram: (programPath?: string) => Promise<KillResult>;

  /**
   * Mata múltiples procesos por nombre
   * @param processNames - Array de nombres de procesos
   */
  killMultipleProcesses: (processNames: string[]) => Promise<Record<string, KillResult>>;

  // ========== Path Checking ==========

  /**
   * Verifica si una aplicación/archivo existe en la ruta especificada
   * Soporta variables de entorno (%VAR% en Windows, $VAR en Unix)
   * @param path - Ruta a verificar
   */
  isAppAvailable: (path?: string) => Promise<boolean>;

  /**
   * Verifica si múltiples aplicaciones/rutas existen
   * @param paths - Array de rutas a verificar
   */
  checkMultiplePaths: (paths: string[]) => Promise<Record<string, boolean>>;

  // ========== Power Management ==========

  /**
   * Ejecuta una acción de power management en el sistema
   * @param action - Acción a ejecutar (shutdown, restart, logout, sleep, hibernate)
   * @param options - Opciones adicionales
   */
  executePowerAction: (action: PowerAction, options?: PowerOptions) => Promise<PowerResult>;

  /**
   * Apaga el PC
   * @param options - Opciones (force, delay, message)
   */
  shutdown: (options?: PowerOptions) => Promise<PowerResult>;

  /**
   * Reinicia el PC
   * @param options - Opciones (force, delay, message)
   */
  restart: (options?: PowerOptions) => Promise<PowerResult>;

  /**
   * Cierra sesión del usuario actual
   * @param options - Opciones (force)
   */
  logout: (options?: PowerOptions) => Promise<PowerResult>;

  /**
   * Pone el PC en modo suspensión
   * @param options - Opciones
   */
  sleep: (options?: PowerOptions) => Promise<PowerResult>;

  /**
   * Hiberna el PC
   * @param options - Opciones
   */
  hibernate: (options?: PowerOptions) => Promise<PowerResult>;

  /**
   * Cancela un shutdown programado (solo Windows)
   */
  cancelScheduledShutdown: () => Promise<boolean>;

  // ========== Named Programs ==========

  /**
   * Verifica si la aplicación Comitas Net está disponible en el sistema
   */
  isComitasNetAppAvailable: () => Promise<boolean>;

  /**
   * Abre la aplicación Comitas Net desde el escritorio
   */
  openComitasNetApp: () => Promise<OpenProgramResult>;
}
